import React from "react";
import { Box, Card, styled, Typography, useTheme } from "@mui/material";
import { CenteredSiteBlock } from "../../routes/Home";
import { GradientTypo } from "../header/Header";

import { ReactComponent as EnlargeKnowledgeIcon } from "../../resources/images/svg/home/enlargeYourKnowledge.svg";
import { ReactComponent as PodcastIcon } from "../../resources/images/svg/home/podcast.svg";
import { ReactComponent as ZoomIcon2 } from "../../resources/images/svg/home/zoomIcon2.svg";
import { ReactComponent as KnowledgeItem } from "../../resources/images/svg/home/knowledgeItem.svg";
import CircularDiagram from "./CircularDiagram";
import useIsOnScreen from "../../hooks/useIsOnScreen";
import { useIsMobile } from "../../hooks/responsive";
import SpecialCard from "../card/SpecialCard";

const circleItems = [
  {
    label: "Conseils de thérapeutes aux thérapeutes",
    fill: "rgb(71, 112, 238)",
    textColor: "white",
  },
  {
    label: "Interviews croisées",
    fill: "#AC53F2",
    textColor: "white",
  },
  {
    label: "Écoute de l'extraordinaire",
    fill: "rgb(235, 86, 56)",
    textColor: "white",
  },
  {
    label: "Éthique et posture thérapeutique",
    fill: "#FC7D06",
    textColor: "white",
  },
  {
    label: "Décryptages de pratiques",
    fill: "#F7E144",
    textColor: "#202020",
  },
  {
    label: "Études de cas cliniques",
    fill: "#0596A1",
    textColor: "white",
  },
];

const Container = styled(Box)(({ theme }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: "100px",
    background: "white",
  };
});

const EnlargeKnowledge = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  return (
    <Container className={"ambitions"}>
      <CenteredSiteBlock>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            mt: 7,
            mb: 7,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <EnlargeKnowledgeIcon
              width={"62px"}
              height={"62px"}
              style={{
                marginRight: "20px",
                marginBottom: isMobile ? "20px" : undefined,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 500,
                margin: 0,
                textAlign: "center",
              }}
            >
              Élargissez vos connaissances
            </Typography>
            <GradientTypo
              variant={"h2"}
              background={theme.palette.linearPro}
              fontSize={null}
              sx={{
                fontFamily: "YesevaOne",
                textTransform: "lowercase",
                wordWrap: "break-word",
                maxWidth: "100%",
                textAlign: "center",
                margin: 0,
                fontWeight: 500,
                "&:first-letter": {
                  textTransform: "lowercase !important",
                },
              }}
            >
              &nbsp;{`multidimensionnelles`}
            </GradientTypo>
          </Box>
          <Card
            sx={{
              mt: 5,
              mb: 5,
              padding: (theme) => `${theme.spacing(3)} ${theme.spacing(5)}`,
              backgroundColor: (theme) => theme.palette.grey["200"],
              borderRadius: "20px",
              marginRight: "5vw",
              marginLeft: "5vw",
            }}
          >
            <Typography fontWeight={500}>
              Parce qu’un praticien qui continue d’enrichir ses connaissances
              reste vivant dans sa pratique, est mieux ajusté aux besoins du
              monde et toujours plus pertinent dans ses accompagnements.
            </Typography>
          </Card>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontFamily: "Branding Bold",
              margin: 0,
              textAlign: "center",
              width: "100%",
              textWrap: "wrap",
            }}
          >
            Chaque mercredi : des contenus pour les professionnels
          </Typography>

          <CircularDiagram
            items={circleItems}
            offset={isMobile ? 150 : 220}
            circularItemComponent={EnlargeKnowledgeItem}
            sx={{
              zIndex: 1,
              marginTop: isMobile ? 5 : 0,
            }}
          >
            <ZoomIcon2
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: `translate(-50%, -50%) ${isMobile ? "scale(0.75)" : ""}`,
                zIndex: -1,
              }}
            />
            <SpecialCard
              containerProps={{
                sx: {
                  transform: isMobile ? "scale(0.9)" : undefined,
                },
              }}
              sx={{
                zIndex: 1,
                background: "#F3F2F5",
                boxShadow: (theme) => theme.shadows[25],
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  maxWidth: "140px",
                }}
              >
                <PodcastIcon width={80} height={80} />
                <Typography
                  variant={"h5"}
                  textAlign={"center"}
                  lineHeight={"1.2em"}
                >
                  Podcasts hebdomadaires
                </Typography>
                <Typography
                  variant={"p"}
                  textAlign={"center"}
                  lineHeight={"1em"}
                  mt={1.5}
                >
                  Sur votre espace Praticiens
                </Typography>
              </Box>
            </SpecialCard>
          </CircularDiagram>
        </Box>
      </CenteredSiteBlock>
    </Container>
  );
};

export default EnlargeKnowledge;

const EnlargeKnowledgeItem = ({ item, transformValue }) => {
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        position: "absolute",
        transform: transformValue, // Position based on dynamic angle
        transformOrigin: "50% 50%",
        width: { xs: "25%", sm: "18%", md: "15%" },
        aspectRatio: "1 / 1",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          transition: "transform .3s ease",
          transform: isMobile ? "scale(0.9)" : undefined,
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        <KnowledgeItem
          width={isMobile ? 170 : 200}
          height={isMobile ? 170 : 200}
          style={{
            fill: item.fill,
            "--first-stop-color": "white",
            "--second-stop-color": item.fill,
            filter: "saturate(200%)",
          }}
        />
        <Typography
          variant={"h6"}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "150px" : "170px",
            textAlign: "center",
            fontSize: isMobile ? "18px" : "21px",
            fontWeight: 500,
            margin: 0,
            lineHeight: "1.2em",
            color: item.textColor,
          }}
        >
          {item.label}
        </Typography>
      </Box>
    </Box>
  );
};
